import Head from "expo-router/head";

import {
  canonicalUrl,
  ogImageDimensions,
  ogImageUrl,
  type MarketingMeta,
} from "../lib/marketingMeta";

/**
 * Per-page <head> for public marketing surfaces. Bundles `<title>`,
 * meta description, OpenGraph, and Twitter Card tags into a single
 * import so every public page renders the same shape with consistent
 * field coverage.
 *
 * Usage:
 *   import { MarketingHead } from '../components/MarketingHead';
 *   import { META_PRICING } from '../lib/marketingMeta';
 *   ...
 *   <MarketingHead meta={META_PRICING} />
 *
 * Per-page copy lives in `lib/marketingMeta.ts`. Don't pass inline
 * strings here — that's how og:title and twitter:title silently drift
 * apart at rebrand time.
 */
export function MarketingHead({ meta }: { meta: MarketingMeta }) {
  const url = canonicalUrl(meta.pathname);
  const image = ogImageUrl();
  const { width, height } = ogImageDimensions();
  return (
    <Head>
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <link rel="canonical" href={url} />

      {/* OpenGraph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Coravu" />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content={width} />
      <meta property="og:image:height" content={height} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={image} />
    </Head>
  );
}
